import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";

const ChartDisplay = ({ data, chartType, chartId }) => {
  const processedData = useMemo(() => {
    if (!data) return [];

    return data.program_submission_stats.map((item) => ({
      name: item.program_title,
      value: item.total_program_submissions,
    }));
  }, [data]);

  const colors = [
    "#5470c6",
    "#91cc75",
    "#fac858",
    "#ee6666",
    "#73c0de",
    "#3ba272",
    "#fc8452",
    "#9a60b4",
  ];

  const pieAndDonutOptions = () => ({
    tooltip: {
      trigger: "item",
      formatter: "{a} <br/>{b}: {c} ({d}%)",
    },
    legend: {
      orient: "vertical",
      left: 300,
      top: 60,
      type: "scroll",
    },
    series: [
      {
        name: "Program Submissions",
        type: "pie",
        radius: chartType === "donut" ? ["20%", "45%"] : "45%",
        center: ["25%", "30%"],
        data: processedData.map((item, idx) => ({
          name: item.name,
          value: item.value,
          itemStyle: {
            color: colors[idx % colors.length],
            borderWidth: 2,
            borderColor: "#fff",
            borderRadius: 8,
          },
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
            borderWidth: 3,
          },
          label:
            chartType === "donut"
              ? { show: true, fontSize: 18, fontWeight: "bold" }
              : undefined,
        },
        label:
          chartType === "donut"
            ? { show: false }
            : { formatter: "{b}: {c} ({d}%)" },
        labelLine: chartType === "donut" ? { show: false } : undefined,
        animation: true,
        animationType: "scale",
        animationEasing: "elasticOut",
        animationDelay: (idx) => idx * 100,
      },
    ],
  });

  const barOptions = () => ({
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "5%",
      top: "10%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: processedData.map((item) => item.name),
      axisLabel: {
        interval: 0,
        rotate: 30,
      },
    },
    yAxis: { type: "value" },
    series: [
      {
        name: "Submissions",
        type: "bar",
        data: processedData.map((item) => item.value),
        itemStyle: {
          color: (params) => colors[params.dataIndex % colors.length],
        },
        animation: true,
        animationType: "expansion",
        animationDuration: 1000,
        animationDelay: (idx) => idx * 50,
      },
    ],
  });

  const radarOptions = () => {
    const maxVal = Math.max(...processedData.map((d) => d.value)) || 1;
    return {
      tooltip: {},
      legend: {
        data: ["Submission Count"],
        bottom: 0,
      },
      radar: {
        center: ["50%", "28%"], // position: [horizontal%, vertical%]
        radius: "40%",
        indicator: processedData.map((item) => ({
          name: item.name,
          max: maxVal * 1.2,
        })),
      },
      series: [
        {
          name: "Program Statistics",
          type: "radar",
          data: [
            {
              value: processedData.map((item) => item.value),
              name: "Submission Count",
              areaStyle: { color: "rgba(84, 112, 198, 0.6)" },
              lineStyle: { color: "#5470c6" },
              itemStyle: { color: "#5470c6" },
            },
          ],
          animation: true,
          animationDuration: 1000,
          animationEasing: "quadraticOut",
        },
      ],
    };
  };

  const getChartOptions = () => {
    if (!processedData.length) return {};
    switch (chartType) {
      case "pie":
      case "donut":
        return pieAndDonutOptions();
      case "bar":
        return barOptions();
      case "radar":
        return radarOptions();
      default:
        return {};
    }
  };

  if (!processedData.length) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
        No data available for chart
      </div>
    );
  }

  return (
    <div id={chartId}>
      <ReactECharts
        key={chartType}
        option={getChartOptions()}
        style={{ height: "500px", width: "100%" }}
        opts={{ renderer: "canvas" }}
        notMerge
        lazyUpdate={false}
      />
    </div>
  );
};

export default ChartDisplay;
